/**
 * GlossaryHarvesterDialog — Claude scans the document for acronyms
 * and jargon not yet in the firm glossary. User accepts each entry;
 * accepted terms are POSTed to the existing /glossary endpoint
 * individually (so partial success is recoverable).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';

interface Proposed {
  term: string;
  proposedExpansion: string;
  kind: 'acronym' | 'jargon';
  sourceQuote: string;
}

interface Entry extends Proposed {
  accepted: boolean;
  /** Per-entry save state so partial failures are surfaced cleanly. */
  saveState: 'idle' | 'saving' | 'saved' | 'error';
  error?: string;
  /** Local edits to the expansion field before accepting. */
  expansionDraft: string;
}

interface UsageMeta {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meetingId: string;
}

export function GlossaryHarvesterDialog({
  isOpen,
  onClose,
  meetingId,
}: Props) {
  const qc = useQueryClient();
  const [entries, setEntries] = useState<Entry[]>([]);

  const scan = useMutation({
    mutationFn: () =>
      api.post<{ proposed: Proposed[]; usage: UsageMeta }>(
        `/meetings/${meetingId}/harvest-glossary`,
      ),
    onSuccess: (data) => {
      setEntries(
        data.proposed.map((p) => ({
          ...p,
          accepted: true,
          saveState: 'idle',
          expansionDraft: p.proposedExpansion,
        })),
      );
    },
  });

  useEffect(() => {
    if (isOpen && !scan.data && !scan.isPending && !scan.isError) {
      scan.mutate();
    }
    if (!isOpen) {
      scan.reset();
      setEntries([]);
    }
  }, [isOpen]);

  const acceptedCount = entries.filter(
    (e) => e.accepted && e.saveState !== 'saved',
  ).length;

  const addSelected = async () => {
    const selected = entries
      .map((e, i) => ({ e, i }))
      .filter(({ e }) => e.accepted && e.saveState !== 'saved');
    if (selected.length === 0) {
      onClose();
      return;
    }
    // Fire all requests in parallel but track per-entry state so
    // partial successes stay visible + re-runnable.
    await Promise.all(
      selected.map(async ({ e, i }) => {
        setEntries((arr) => {
          const next = [...arr];
          next[i] = { ...next[i], saveState: 'saving', error: undefined };
          return next;
        });
        try {
          await api.post('/glossary', {
            term: e.term.trim(),
            expansion: e.expansionDraft.trim(),
            kind: e.kind,
          });
          setEntries((arr) => {
            const next = [...arr];
            next[i] = { ...next[i], saveState: 'saved' };
            return next;
          });
        } catch (err) {
          const message =
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Save failed';
          setEntries((arr) => {
            const next = [...arr];
            next[i] = { ...next[i], saveState: 'error', error: message };
            return next;
          });
        }
      }),
    );
    qc.invalidateQueries({ queryKey: ['glossary'] });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Harvest glossary entries"
    >
      <ModalHeader
        title="Harvest glossary entries"
        subtitle="Claude scans the meeting for acronyms and jargon missing from the firm glossary. Edit the expansion if needed, then add the ones you want."
        onClose={onClose}
      />
      <ModalBody>
        {scan.isPending && (
          <div className="py-10 text-center text-sm italic text-charcoal/60">
            Scanning for undefined terms…
          </div>
        )}
        {scan.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {scan.error instanceof ApiError
              ? scan.error.message
              : 'Scan failed'}
          </div>
        )}
        {scan.data && entries.length === 0 && (
          <div className="rounded-2xl border border-dashed border-charcoal/20 p-6 text-center text-sm italic text-charcoal/60">
            No new glossary candidates found. Every acronym / jargon
            term in this meeting is already in the firm glossary.
          </div>
        )}
        {entries.length > 0 && (
          <ul className="space-y-2">
            {entries.map((e, i) => (
              <li
                key={`${e.term}-${i}`}
                className={
                  'rounded-2xl border p-3 transition-colors ' +
                  (e.saveState === 'saved'
                    ? 'border-pine/40 bg-pine/10 opacity-75'
                    : e.accepted
                      ? 'border-pine/30 bg-pine/5'
                      : 'border-charcoal/15 bg-white')
                }
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={e.accepted}
                    onChange={(ev) =>
                      setEntries((arr) => {
                        const next = [...arr];
                        next[i] = { ...next[i], accepted: ev.target.checked };
                        return next;
                      })
                    }
                    disabled={e.saveState === 'saved'}
                    className="mt-1 h-4 w-4 accent-pine"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-charcoal/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-charcoal/70">
                        {e.kind}
                      </span>
                      <span className="font-mono text-sm font-semibold text-charcoal">
                        {e.term}
                      </span>
                      {e.saveState === 'saved' && (
                        <span className="rounded-full bg-pine px-2 py-0.5 text-[10px] font-semibold text-cloud">
                          ✓ Added
                        </span>
                      )}
                      {e.saveState === 'saving' && (
                        <span className="text-[10px] italic text-charcoal/60">
                          Saving…
                        </span>
                      )}
                      {e.saveState === 'error' && (
                        <span className="text-[10px] text-red-600">
                          {e.error}
                        </span>
                      )}
                    </div>
                    <input
                      {...NO_AUTOFILL}
                      type="text"
                      value={e.expansionDraft}
                      onChange={(ev) =>
                        setEntries((arr) => {
                          const next = [...arr];
                          next[i] = {
                            ...next[i],
                            expansionDraft: ev.target.value,
                          };
                          return next;
                        })
                      }
                      disabled={e.saveState === 'saved'}
                      className="mt-2 w-full rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20 disabled:opacity-60"
                      placeholder="Expansion / definition"
                    />
                    <details className="mt-1.5 text-[11px] text-charcoal/60">
                      <summary className="cursor-pointer hover:text-pine">
                        Source quote
                      </summary>
                      <div className="mt-1 rounded border border-charcoal/10 bg-cloud/40 p-2 italic">
                        &ldquo;{e.sourceQuote}&rdquo;
                      </div>
                    </details>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </ModalBody>
      <ModalFooter className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/60">
          {acceptedCount > 0
            ? `${acceptedCount} to add`
            : entries.length > 0
              ? 'None selected'
              : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Close
          </button>
          <button
            type="button"
            onClick={addSelected}
            disabled={acceptedCount === 0}
            className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
          >
            Add {acceptedCount > 0 ? `${acceptedCount}` : 'selected'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
